import {
    Component,
    Host,
    h,
    State,
    Element,
    Listen,
    Watch,
} from "@stencil/core";
import { debounce } from "../../utils/debounce";
import { throttle } from "../../utils/throttle";

interface ElementRects {
    commonParentElement: HTMLElement;
    commonParentRects: DOMRect;
    targetElements: any;
    calculated: boolean;
}

@Component({
    tag: "ux-iws-same-height",
})
export class SameHeightWrapper {
    private baseClass = "ux-iws-same-height";

    @Element() element: HTMLUxIwsSameHeightElement;

    @State() sameHeightIdentifiers: string[] = [];
    @State() elementsRects: ElementRects[] = [];
    @State() commonParentElements: Element[];

    private getCommonParentElements = () => {
        const targets = this.element.querySelectorAll(`*[data-same-height]`);
        const range = new Range();
        range.setStartBefore(targets[0]);
        range.setEndAfter(targets[targets.length - 1]);
        this.commonParentElements = Array.from(
            (range.commonAncestorContainer as HTMLElement).children
        );
    };

    private getElementsRects = () => {
        const elementsRects: ElementRects[] = [];
        if (!this.commonParentElements) {
            return;
        }
        this.commonParentElements.forEach((commonParent: HTMLElement) => {
            const commonParentElement = commonParent;
            // do not include in the calculation items that have been hidden from the static filter;
            if (
                commonParentElement.classList.contains(
                    "ux-static-filter__item--hidden"
                )
            )
                return;
            const commonParentRects = commonParent.getBoundingClientRect();
            let targetElementsRects = {};
            this.sameHeightIdentifiers.forEach((sameHeightIdentifier) => {
                const targetElement: HTMLElement = commonParent.querySelector(
                    `*[data-same-height="${sameHeightIdentifier}"]`
                );
                if (targetElement !== null) {
                    const targetElementRects =
                        targetElement.getBoundingClientRect();
                    targetElementsRects = {
                        ...targetElementsRects,
                        [sameHeightIdentifier]: {
                            targetElement,
                            targetElementRects,
                        },
                    };
                }
            });
            elementsRects.push({
                commonParentElement,
                commonParentRects,
                targetElements: targetElementsRects,
                calculated: false,
            });
        });
        this.elementsRects = elementsRects;
    };

    private getSameHeightIdentifiers = () => {
        const sameHeightElements = this.element.querySelectorAll(
            "*[data-same-height]"
        );
        const identifiers = [];
        sameHeightElements.forEach((element: HTMLElement) => {
            identifiers.push(element.dataset.sameHeight);
        });
        const uniqueIdentifiers = [];
        for (let i = 0; i < identifiers.length; i++) {
            if (!uniqueIdentifiers.includes(identifiers[i])) {
                uniqueIdentifiers.push(identifiers[i]);
            }
        }
        this.sameHeightIdentifiers = uniqueIdentifiers;
    };

    private addLazyLoadListeners = () => {
        this.element.querySelectorAll("img").forEach((img) => {
            if (!img.complete || !(img.src || img.srcset)) {
                img.addEventListener("load", this.recalculateDebounced);
            }
        });
        this.element.querySelectorAll("video").forEach((video) => {
            if (Number.isNaN(video.duration)) {
                video.addEventListener(
                    "loadedmetadata",
                    this.recalculateDebounced
                );
            }
        });
    };

    componentWillLoad() {
        this.getSameHeightIdentifiers();
        this.addLazyLoadListeners();
    }

    componentDidLoad() {
        this.getCommonParentElements();
        this.getElementsRects();
    }

    @Watch("elementsRects")
    updateHeight() {
        this.updateElementsHeight();
    }

    private updateElementsHeight = () => {
        for (let i = 0; i < this.elementsRects.length; i++) {
            const sameLineElements = this.elementsRects.filter(
                (elementRects) => {
                    return (
                        elementRects.commonParentRects.top ===
                        this.elementsRects[i].commonParentRects.top
                    );
                }
            );
            for (let v = 0; v < this.sameHeightIdentifiers.length; v++) {
                let biggestHeight = 0;
                for (let i = 0; i < sameLineElements.length; i++) {
                    const element =
                        sameLineElements[i].targetElements[
                            this.sameHeightIdentifiers[v]
                        ];
                    let height = 0;

                    if (element?.targetElementRects) {
                        this.preventVerticalMarginCollapse(
                            element.targetElement
                        );
                        height = element.targetElement.scrollHeight;
                    }
                    if (biggestHeight < height) biggestHeight = height;
                }
                sameLineElements.forEach((element) => {
                    const targetElement =
                        element.targetElements[this.sameHeightIdentifiers[v]];
                    if (targetElement) {
                        targetElement.targetElement.style.minHeight = `${biggestHeight}px`;
                    }
                });
            }
        }
    };

    private removeHeights = () => {
        for (let v = 0; v < this.sameHeightIdentifiers.length; v++) {
            this.elementsRects.forEach((elementRects) => {
                const targetElement =
                    elementRects.targetElements[this.sameHeightIdentifiers[v]];
                if (!targetElement) return;
                targetElement.targetElement.style.minHeight = "auto";
            });
        }
    };

    private removeHeightsThrottled = throttle(this.removeHeights, 100);
    private getElementsRectsDebounced = debounce(this.getElementsRects, 100);

    private recalculateDebounced = () => {
        this.removeHeightsThrottled();
        this.getElementsRectsDebounced();
    };

    @Listen("resize", { target: "window" })
    handleResize() {
        this.recalculateDebounced();
    }

    @Listen("uxActiveTabSet", { target: "window" })
    handleTabChange(event) {
        const tabContainer = event.target as HTMLUxTabContainerElement;
        if (
            !tabContainer.anchorNavigation &&
            tabContainer.contains(this.element)
        ) {
            this.recalculateDebounced();
        }
    }

    private preventVerticalMarginCollapse = (targetElement: HTMLElement) => {
        const targetElementStyles = window.getComputedStyle(targetElement);
        const paddingTop = targetElementStyles.getPropertyValue("padding-top");
        const paddingBottom =
            targetElementStyles.getPropertyValue("padding-bottom");
        if (paddingTop === "0px") targetElement.style.paddingTop = "0.05px";
        if (paddingBottom === "0px")
            targetElement.style.paddingBottom = "0.05px";
    };

    render() {
        return (
            <Host class={this.baseClass}>
                <slot />
            </Host>
        );
    }
}
